import { ReactiveController, ReactiveControllerHost } from "lit";
import { RatingSet, RatingConfig } from "../models";
import { decodeRatings, upgradeStateToVersion, resolveHighestVersion } from "../utils/serialization";
import { findDistinctColor, validateColorDistinctness } from "../utils/color";
import { PersistenceController } from "./persistence-controller";
import { ToastController } from "./toast-controller";

/**
 * Manages dataset manipulation logic (add, duplicate, remove, import).
 * Offloads complex state transitions from the main app component.
 */
export class DatasetController implements ReactiveController {
  host: ReactiveControllerHost & { 
    _activeSetIndex: number;
    locked: boolean;
    config: RatingConfig;
    _chartBackground(): string;
  };

  private _persistence: PersistenceController;
  private _toasts: ToastController;
  private _beforeUpdate: (() => void) | undefined;

  constructor(
    host: DatasetController["host"],
    persistence: PersistenceController,
    toasts: ToastController,
    beforeUpdate?: () => void
  ) {
    this.host = host;
    this._persistence = persistence;
    this._toasts = toasts;
    this._beforeUpdate = beforeUpdate;
    host.addController(this);
  }

  hostConnected() {}

  /**
   * Adds a new empty dataset to the state.
   */
  addDataset() {
    this.host.locked = false;
    let baseState = this._persistence.state;
    const config = this.host.config;
    
    // Upgrade if necessary
    const currentVersion = config.currentVersion;
    const activeVersion = baseState.version ?? currentVersion;
    if (activeVersion !== currentVersion) {
      baseState = upgradeStateToVersion(baseState, currentVersion, config);
    }

    const versionConfig = config.versions[currentVersion];
    const defaultRatings: Record<string, number | null> = {};
    for (const id of versionConfig.attributeIds) {
      defaultRatings[id] = null;
    }

    const existingColors = baseState.datasets.map(
      (set, i) => set.color || config.datasetColors[i % config.datasetColors.length]
    );
    const color = findDistinctColor([this.host._chartBackground(), ...existingColors]) || 
                 config.datasetColors[baseState.datasets.length % config.datasetColors.length];

    const newSet: RatingSet = {
      ratings: defaultRatings,
      name: `${baseState.datasets.length + 1}`,
      color
    };
    
    const newDatasets = [...baseState.datasets, newSet];
    this._beforeUpdate?.();
    this._persistence.update({
      ...baseState,
      datasets: newDatasets,
      version: currentVersion,
    });
    
    this.host._activeSetIndex = newDatasets.length - 1;
    this._toasts.show("Added new comparison");
  }

  /**
   * Duplicates an existing dataset.
   */
  duplicateDataset(index: number) {
    this.host.locked = false;
    const source = this._persistence.state.datasets[index];
    const existingColors = this._persistence.state.datasets.map(
      (set, i) => set.color || this.host.config.datasetColors[i % this.host.config.datasetColors.length]
    );
    const color = findDistinctColor([this.host._chartBackground(), ...existingColors]) || 
                 this.host.config.datasetColors[this._persistence.state.datasets.length % this.host.config.datasetColors.length];

    const newSet: RatingSet = {
      ratings: { ...source.ratings },
      name: `${source.name || (index + 1)} (Copy)`,
      color
    };
    
    const datasets = [...this._persistence.state.datasets];
    datasets.splice(index + 1, 0, newSet);

    this._beforeUpdate?.();
    this._persistence.update({ ...this._persistence.state, datasets });
    this.host._activeSetIndex = index + 1;
    this._toasts.show("Dataset duplicated");
  }

  /**
   * Removes a dataset by index.
   */
  removeDataset(index: number) {
    if (this.host.locked || this._persistence.state.datasets.length <= 1) return;
    
    const oldActiveIndex = this.host._activeSetIndex;
    const datasets = this._persistence.state.datasets.filter((_, i) => i !== index);
    this._beforeUpdate?.();
    this._persistence.update({ ...this._persistence.state, datasets });
    
    if (index < oldActiveIndex) {
      this.host._activeSetIndex = oldActiveIndex - 1;
    } else if (oldActiveIndex >= datasets.length) {
      this.host._activeSetIndex = datasets.length - 1;
    }
    
    this._toasts.show("Dataset removed", "info");
  }

  /**
   * Imports datasets from a URL or hash.
   */
  importDataset(url: string) {
    try {
      const config = this.host.config;
      const hash = url.includes("#") ? url.slice(url.indexOf("#")) : `#${url}`;
      const state = decodeRatings(hash, config);
      
      if (state.datasets.length > 0) {
        this.host.locked = false;
        const bg = this.host._chartBackground();
        const existingSets = this._persistence.state.datasets.map((set, i) => ({
          ...set,
          color: set.color || config.datasetColors[i % config.datasetColors.length],
        }));

        let recoloredCount = 0;
        const importedSets = state.datasets.map((set) => {
          const committed = existingSets.map(s => s.color!);
          const incomingColor = set.color || config.datasetColors[existingSets.length % config.datasetColors.length];
          const { valid } = validateColorDistinctness([incomingColor, bg, ...committed]);

          const finalColor = (() => {
            if (!valid) {
              recoloredCount++;
              return findDistinctColor([bg, ...committed]) ?? incomingColor;
            }
            return incomingColor;
          })();

          existingSets.push({ ...set, color: finalColor });
          return { ...set, color: finalColor };
        });

        const newSets = [...this._persistence.state.datasets.map((set, i) => ({
          ...set,
          color: set.color || config.datasetColors[i % config.datasetColors.length],
        })), ...importedSets];

        const existingVersion = this._persistence.state.version ?? config.currentVersion;
        const importedVersion = state.version ?? config.currentVersion;
        const mergedVersion = resolveHighestVersion(
          [existingVersion, importedVersion, config.currentVersion],
          config
        );

        const mergedState = upgradeStateToVersion(
          { ...this._persistence.state, datasets: newSets, version: mergedVersion },
          mergedVersion,
          config,
        );
        this._beforeUpdate?.();
        this._persistence.update(mergedState);
        this.host._activeSetIndex = this._persistence.state.datasets.length - 1;

        const label = state.datasets.length === 1 ? "1 dataset" : `${state.datasets.length} datasets`;
        const recolorNote = recoloredCount > 0
          ? ` (${recoloredCount} color${recoloredCount > 1 ? "s" : ""} adjusted for visibility)`
          : "";
        this._toasts.show(`Imported ${label}${recolorNote}`);
        return true;
      } else {
        this._toasts.show("Invalid URL or hash", "warn");
      }
    } catch {
      this._toasts.show("Failed to import", "warn");
    }
    return false;
  }
}
